import { Container, Stack, Link } from '@mui/material';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getCurrentWorkspaceUser,
  getUser,
  acceptWorkspaceInvite,
  getCurrentWorkspaceSKU,
} from 'wisestep-api';
import {
  WorkspaceCard,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  getDaysBetweenDates,
  IWorkspace,
  IUserDTO,
  Spinner,
} from 'wisestep-base';

import { AuthLayout } from '../../../components';
import { BlockedUserNotification } from '../../../components/BlockedUserNotification';
import { useWorkspaceContext, useUserContext, useWorkspaceSKUContext } from '../../../context';
import { BLOCKED_USERS } from '../../../utils/Constants';
import { decodedParamsWithExceptionHandled } from '../../../utils/getDecodedQueryParams';
import { success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { getNavURL, setNavURL } from '../../../utils/token';

import AcessRestrictionWarning from './AcessRestrictionWarning';

const AlertBar = styled(Alert)(() => ({
  width: '100%',
  pl: '32px',
}));

const WorkspaceListing = () => {
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);

  const expiredDateParams: { expiryDate: string } | null =
    decodedParamsWithExceptionHandled(urlParams.get('date') ?? '') ?? null;

  const navigate = useNavigate();

  const user = useUserContext();

  const setNotification = useNotificationStore((state) => state.setNotification);

  const loggedInUser = useUserContext().userDTO;

  const workspaceSKU = useWorkspaceSKUContext();

  const workspaceContext = useWorkspaceContext();

  const [ownedWorkspace, setOwnedWorkspace] = useState<IWorkspace[]>([]);

  const [sharedWorkspace, setSharedWorkspace] = useState<IWorkspace[]>([]);

  const [invitedWorkspace, setInvitedWorkspace] = useState<IWorkspace[]>([]);

  const [planExpireDate, setPlanExpireDate] = useState<string | null>(
    expiredDateParams ? expiredDateParams.expiryDate : null
  );

  const [loadPage, setLoadPage] = useState<boolean>(false);

  const [isUserBlocked, setIsUserBlocked] = useState(false);

  useEffect(() => {
    if (location.search.indexOf('?blocked=') > -1) {
      setIsUserBlocked(true);
    }
  }, [location.search]);

  useEffect(() => {
    (async function () {
      if (!loggedInUser?.id && user.setUser) {
        const userReq = await getUser();
        const userRes: IUserDTO = userReq.data;
        const userState = { ...user };
        userState.userDTO = userRes;
        user.setUser({ ...userState });
      }
    })();
  }, [loggedInUser, user]);

  useEffect(() => {
    const url = window.location.origin.split('.');
    if (workspaceContext.id && workspaceContext['onBoardingQuestionAnswered'] === false) {
      navigate('/onboarding-questions');
    }
    if (workspaceContext.link && url[0].split('//')[1] === workspaceContext.link) {
      if (!isEmpty(getNavURL())) {
        const url = getNavURL() as string;
        setNavURL(null);
        navigate(url);
      } else {
        navigate('/home');
      }
    }
  }, [navigate, workspaceContext]);

  useEffect(() => {
    (async function () {
      const workspaceReq = await getCurrentWorkspaceUser();
      setLoadPage(true);
      const workspaceRes: IWorkspace[] = workspaceReq.data;
      if (workspaceRes && workspaceRes.length > 0) {
        setOwnedWorkspace(workspaceRes.filter((workspace: IWorkspace) => workspace.owns === true));
        setSharedWorkspace(
          workspaceRes.filter(
            (workspace: IWorkspace) => workspace.owns === false && workspace.accepted === true
          )
        );
        setInvitedWorkspace(
          workspaceRes.filter(
            (workspace: IWorkspace) => workspace.accepted === false && workspace.owns === false
          )
        );
      } else {
        navigate('/workspace/create-workspace');
      }
    })();
  }, [navigate]);

  const openSubscriptionLink = (link: string | undefined) => (
    <Link
      component="button"
      variant="body2"
      onClick={() => window.open(`${link}.wisestep.com/#/recruiter/subscription`, '_blank')}
    >
      Subscribe now
    </Link>
  );

  const expireNotification = (workspace: IWorkspace) => {
    const expiryDate = workspace.planEndDate;
    if (expiryDate) {
      const workspaceExpiryDate = getDaysBetweenDates(new Date(), expiryDate);
      if (workspaceExpiryDate < 7) {
        if (workspaceExpiryDate < 0) {
          return (
            <>
              <AlertBar severity="warning">
                Your subscription expired. <br />
                &nbsp;{openSubscriptionLink(workspace.link)}
              </AlertBar>
            </>
          );
        } else if (workspaceExpiryDate >= 0) {
          return (
            <>
              <AlertBar severity="warning">
                Your subscription will expire in {workspaceExpiryDate} day(s) &nbsp;
                {openSubscriptionLink(workspace.link)}
              </AlertBar>
            </>
          );
        }
      }
    }
  };

  const openWorkspace = async (workspace: IWorkspace) => {
    if (loggedInUser?.email && BLOCKED_USERS.includes(loggedInUser?.email)) {
      setIsUserBlocked(true);
    } else {
      const url = window.location.origin.split('.');
      if (
        url[0].split('//')[1] === workspace.link &&
        workspace['onBoardingQuestionAnswered'] === false &&
        workspace.owns === true &&
        workspace.barred
      ) {
        navigate('/onboarding');
      } else if (workspace.barred == true) {
        setNotification(
          failed(
            'You are not allowed to access this workspace. Please contact workspace owner to get the access.'
          )
        );
      } else {
        const res = await getCurrentWorkspaceSKU(workspace.id);
        const validSKU = res.data.find((item) => new Date(item?.expiryDate) > new Date());
        const planExpireDate = validSKU ? validSKU.expiryDate : res.data[0].expiryDate;
        workspaceSKU.setWorkspaceSKU && workspaceSKU.setWorkspaceSKU(res.data[0]);
        if (planExpireDate && dayjs(planExpireDate).diff(new Date(), 'd') < 1 && workspace.owns) {
          const workspaceURL = `//${workspace.link}.${url[1]}.${url[2]}/plan/purchase`;
          window.location.href = workspaceURL;
        } else if (planExpireDate && dayjs(planExpireDate).diff(new Date(), 'd') < -7) {
          setPlanExpireDate(dayjs(planExpireDate).format('dddd, MMMM D, YYYY h:mm A'));
        } else {
          const workspaceURL = `//${workspace.link}.${url[1]}.${url[2]}/home`;
          window.location.href = workspaceURL;
        }
      }
    }
  };

  const acceptWorkspace = async (workspace: IWorkspace) => {
    try {
      await acceptWorkspaceInvite({
        id: workspace.id,
        roles: workspace.roles,
        role: workspace.role,
      });
      setNotification(success('Workspace joined.'));
      openWorkspace(workspace);
    } catch (e) {
      setNotification(failed('Some error occurred. Please try later'));
    }
  };

  return (
    <>
      {loadPage ? (
        <AuthLayout>
          <Container maxWidth="sm" id="workspace-listing">
            <Stack direction="column" alignItems="center" spacing={1}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                {'Welcome back ' + loggedInUser?.firstName + '!'}
              </WsTypography>
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SENARY}>
                Choose your workspace to get started
              </WsTypography>
            </Stack>
            <Stack mt={3} spacing={1} direction="column">
              {invitedWorkspace && invitedWorkspace.length ? (
                <WsTypography color={WsTypoColors.TERTIARY} variant={WsTypoVariants.BODY1}>
                  INVITED WORKSPACE
                </WsTypography>
              ) : null}
              {invitedWorkspace.map((workspace: IWorkspace, index: number) => (
                <WorkspaceCard
                  key={index}
                  workspace={workspace}
                  expireNotification={expireNotification}
                  openWorkspace={acceptWorkspace}
                  isAvatarColorBlue={true}
                />
              ))}
              {ownedWorkspace && ownedWorkspace.length ? (
                <WsTypography color={WsTypoColors.TERTIARY} variant={WsTypoVariants.BODY1}>
                  YOUR WORKSPACE
                </WsTypography>
              ) : null}
              {ownedWorkspace.map((workspace: IWorkspace, index: number) => (
                <WorkspaceCard
                  key={index}
                  workspace={workspace}
                  expireNotification={expireNotification}
                  openWorkspace={openWorkspace}
                  isAvatarColorBlue={true}
                />
              ))}
              <br />
              {sharedWorkspace && sharedWorkspace.length ? (
                <WsTypography color={WsTypoColors.TERTIARY} variant={WsTypoVariants.BODY1}>
                  SHARED WORKSPACE
                </WsTypography>
              ) : null}
              {sharedWorkspace.map((workspace: IWorkspace, index: number) => (
                <WorkspaceCard
                  key={index}
                  workspace={workspace}
                  expireNotification={expireNotification}
                  openWorkspace={openWorkspace}
                  isAvatarColorBlue={false}
                />
              ))}
            </Stack>
          </Container>
        </AuthLayout>
      ) : (
        <Spinner />
      )}
      {planExpireDate && (
        <AcessRestrictionWarning
          expireDate={planExpireDate}
          onClose={() => setPlanExpireDate(null)}
        />
      )}
      {isUserBlocked && <BlockedUserNotification onClose={() => setIsUserBlocked(false)} />}
    </>
  );
};

export default WorkspaceListing;
